import { fetchErrorStatus } from '@/remote/dashboard/error-status';
import { QUERY_KEYS } from '@/remote/query-key';
import type { ErrorStatus } from '@/types/site';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseSiteErrorStatusOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
}

interface UseSiteErrorStatusReturn {
  data: ErrorStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  actions: {
    refetch: () => void;
    invalidate: () => void;
  };
}

export const useSiteErrorStatus = (
  siteId: string,
  options: UseSiteErrorStatusOptions = {}
): UseSiteErrorStatusReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 30, // 30초
    gcTime = 1000 * 60 * 5, // 5분
    refetchInterval = 1000 * 60, // 🚀 1분마다 에러 상태 확인
  } = options;

  const query = useQuery<ErrorStatus>({
    queryKey: QUERY_KEYS.dashboard.errorStatus(siteId),
    queryFn: () => fetchErrorStatus(siteId),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { refetch } = query;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.errorStatus(siteId),
    });
  }, [queryClient, siteId]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    actions: {
      refetch,
      invalidate,
    },
  };
};
